import { Component, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * FAQ "People also ask" accordion section for SignNow brand.
 * Wraps the generic `faq-type-2` component with SignNow theming.
 * The heading and subheading are inline-editable via pure slot pass-through.
 * FAQ items (question + answer) are managed via the `items` prop and editable
 * in the visual editor right panel.
 *
 * @product signnow
 * @source ppa
 * @version 2.0.0
 * @category SignNow
 * @contentConfig FAQ "People also ask" accordion section
 * @slot heading - {required}, <me-heading tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading
 * @slot subheading - <me-text> - Section description text
 * @example
 *   <sn-faq-type-2
 *     items='[{"question":"Are electronic signatures legally binding?","answer":"Yes. Electronic signatures are legally binding in most countries under laws such as ESIGN in the US and eIDAS in the EU."},{"question":"How secure is airSlate SignNow?","answer":"airSlate SignNow uses bank-level 256-bit AES encryption to protect your documents and data."},{"question":"Can I try airSlate SignNow for free?","answer":"Yes. airSlate SignNow offers a free trial with full access to all features \u2014 no credit card required."}]'
 *     help-text="Need help?"
 *     link-text="Contact support"
 *     link-href="https://support.signnow.com"
 *   >
 *     <me-heading slot="heading" variant="2" tag="h2">People also ask</me-heading>
 *     <me-text slot="subheading" tag="p" variant="large">Here is a list of the most common customer questions. If you can't find an answer to your question, please don't hesitate to reach out to us.</me-text>
 *   </sn-faq-type-2>
 */
@Component({
  tag: 'sn-faq-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFAQType2 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * FAQ items list
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} question - The question text
   * @item.property {string} answer - The answer content, supports HTML
   * @item.required question
   * @item.required answer
   * @item.content question answer
   * @minItems 1
   */
  @Prop({ mutable: true }) items!: any[] | string;

  /**
   * Optional lead-in text for the help line shown beneath the description
   * (e.g. "Need help?"). Rendered bold, immediately before the support link.
   * Leave empty to hide the whole help line.
   * @content
   */
  @Prop() helpText: string = '';

  /**
   * Text for the support link rendered after `helpText` (e.g. "Contact support").
   * Styled as an outgoing link with a sliding underline on hover.
   * Leave empty to hide the link.
   * @content
   */
  @Prop() linkText: string = '';

  /**
   * URL the support link points to.
   * @default '#'
   */
  @Prop() linkHref: string = '#';

  componentWillLoad() {
    this.items = parseJsonProp(this.items, []);
  }

  render() {
    const hasHelp = Boolean(this.helpText || this.linkText);

    return (
      <Host data-color-mode={this.colorMode}>
        <faq-type-2 items={this.items as any}>
          <slot name="heading"></slot>
          <slot name="subheading"></slot>
          {hasHelp && (
            <p slot="action-primary" class="sn-faq-type-2__help">
              <strong class="sn-faq-type-2__help-inner">
                {this.helpText}
                {this.helpText && this.linkText ? ' ' : null}
                {this.linkText && (
                  <a class="sn-faq-type-2__help-link" href={this.linkHref} rel="nofollow" target="_blank">
                    {this.linkText}
                  </a>
                )}
              </strong>
            </p>
          )}
        </faq-type-2>
      </Host>
    );
  }
}
